import './SupplyScheduleTable.scss';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { ICustomComponentProps } from '@molecules/formbuilder/controls/CustomComponent/ICustomComponentProps';
import Hint from '@srm/ui/hint';
import {
    ISupplyScheduleItem,
    ISupplyScheduleSettings,
    SupplyMonth,
    SupplyYear,
} from '@models/supplySchedule/ISupplyScheduleTable';
import DataTable, { IDataTableColumn, parseColumnWidth } from '@srm/ui/data-table';
import { formatPrice } from '@utils/helpers';
import Preloader from '@srm/ui/preloader';
import { SupplyScheduleService } from '@services/SupplyScheduleService';
import { ValueType } from '@srm/ui/types/ValueType';
import Tooltip from '@srm/ui/tooltip';
import Switch from '@srm/ui/switch';
import { SegmentedControl } from '@mantine/core';
import { ISegment } from '@srm/ui/types/ISegment';
import { YearsShowType } from '@/types';
import { months } from '@/constants/date';
import { RouteNames } from '@/router/router.types';
import { capitalize } from '@/utils/capitalize';
import SystemLinkList from '@molecules/SystemLink/SystemLinkList';

export type ISupplyScheduleTableProps<TFieldValues extends object = object> = ICustomComponentProps<TFieldValues>;

const YEARS_SEGMENTS: ISegment[] = [
    { value: '0', label: 'Все годы' },
    { value: '1', label: 'Текущий год' },
];

// data-value на ячейках читают конфиг-стили и автотесты. Обработчик вне компонента: новая ссылка
// на каждый рендер сбрасывала бы кэш строк таблицы.
const markCellValue = ({ element, text }: { element: HTMLElement; text: string }) => {
    element.setAttribute('data-value', text);
};

const formatKeyValue = (template: string, data: Record<string, string>): string =>
    template.replace(/{(.*?)}/g, (_, key) => data[key] || '');

/** Годы и месяцы, попадающие в таблицу: по данным, с учётом настроек показа. */
const getUniqueYearsAndMonths = (
    data: ISupplyScheduleItem[],
    hideEmptyMonths: boolean,
    currentYearOnly: boolean,
): Record<SupplyYear, Set<SupplyMonth>> => {
    const yearsMonths: Record<SupplyYear, Set<SupplyMonth>> = {};
    const allMonths: SupplyMonth[] = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
    const currentYearValue = new Date().getFullYear().toString();

    data.forEach((item) => {
        Object.keys(item.data).forEach((year) => {
            if (currentYearOnly && year !== currentYearValue) return;
            yearsMonths[year] ??= new Set();
            if (hideEmptyMonths) {
                Object.keys(item.data[year]).forEach((month) => yearsMonths[year].add(month));
            } else {
                allMonths.forEach((month) => yearsMonths[year].add(month));
            }
        });
    });

    return yearsMonths;
};

type SupplyPosition = NonNullable<ISupplyScheduleItem['data'][string][string]>['positions'][number];

/** Список позиций месяца/года — содержимое тултипа суммы. */
const positionsTooltip = (positions: SupplyPosition[], isAdminView: boolean): ReactNode => (
    <ul className="tooltip-position-list">
        {positions.map((position) => (
            <li key={position.doc_id} className="tooltip-position-item">
                {isAdminView ? (
                    <a
                        href={RouteNames.DOCUMENT.replace(':id', position.doc_id)}
                        className="position-name"
                        target="_blank"
                        rel="noopener,noreferrer"
                    >
                        {position.position_name}
                    </a>
                ) : (
                    <div className="position-name">{position.position_name}</div>
                )}
                <div className="position-cost">Цена: {formatPrice(position.cost_value, 'RUB') || 'Не указана'}</div>
                <div className="position-date">
                    Дата поставки:{' '}
                    {position.supply_date ? new Date(position.supply_date).toLocaleDateString('ru-RU') : 'Не указана'}
                </div>
            </li>
        ))}
    </ul>
);

const sumTooltip = (text: string, content: ReactNode): ReactNode => (
    <Tooltip
        openDelay={100}
        offsetValue={{ mainAxis: 0, crossAxis: 0 }}
        background="transparent"
        hidePadding={true}
        position="top"
        label={content}
    >
        <span>{text}</span>
    </Tooltip>
);

/**
 * График поставки на базовом `@atoms/DataTable`: слева закреплённые ключевые
 * колонки, дальше — месяцы («Январь 2026») и «Итого 2026» по каждому году.
 * Периоды и пустые месяцы — контролы в тулбаре таблицы, строка фильтров —
 * штатная кнопка-воронка.
 */
const SupplyScheduleTable = <TFieldValues extends object = object>({
    component,
    docId,
}: ISupplyScheduleTableProps<TFieldValues>) => {
    const [settings, setSettings] = useState<ISupplyScheduleSettings>();
    const [errorText, setErrorText] = useState<string>();
    const [items, setItems] = useState<ISupplyScheduleItem[]>([]);
    const keys = useMemo(() => Object.keys(settings?.keys ?? {}), [settings]);

    const tableKey = component.params?.tableKey;

    useEffect(() => {
        if (!tableKey) {
            console.error('SupplyScheduleTable. Ошибка загрузки данных (tableKey пустой)');
            return;
        }
        let cancelled = false;
        SupplyScheduleService.fetchTableData(docId, tableKey)
            .then((response) => {
                if (cancelled) return;
                setItems(response.data.data);
                setSettings(response.data.settings);
            })
            .catch((reason) => {
                if (!cancelled) setErrorText(String(reason));
            });
        return () => {
            cancelled = true;
        };
    }, [docId, tableKey]);

    // ----- Настройки показа (контролы тулбара) -----
    const [hideEmptyMonths, setHideEmptyMonths] = useState<boolean>(false);
    const [yearsShowSetting, setYearsShowSetting] = useState<YearsShowType>(YearsShowType.AllYears);

    const yearsMonths = useMemo(
        () => getUniqueYearsAndMonths(items, hideEmptyMonths, yearsShowSetting === YearsShowType.CurrentYear),
        [items, hideEmptyMonths, yearsShowSetting],
    );

    const isAdminView = settings?.isAdminView ?? false;

    const columns = useMemo<IDataTableColumn<ISupplyScheduleItem>[]>(() => {
        if (!settings) return [];
        const cols: IDataTableColumn<ISupplyScheduleItem>[] = keys.map((key) => {
            const meta = settings.keys[key];
            const text = (row: ISupplyScheduleItem): string =>
                String(
                    (meta.displayFormat ? formatKeyValue(meta.displayFormat, row.keyValues) : row.keyValues[key]) ?? '',
                );
            return {
                field: `key:${key}`,
                header: meta.caption,
                visible: meta.visible,
                pinned: 'left',
                // ключевые колонки закреплены кодом — пользователь их не открепляет
                allowPinning: false,
                width: parseColumnWidth(meta.width),
                minWidth: 100,
                accessor: text,
                cellText: text,
                ...(meta.valueType === ValueType.Url && {
                    render: (row: ISupplyScheduleItem) => {
                        const value = text(row);
                        return value ? <SystemLinkList href={value} caption={value} /> : null;
                    },
                }),
            };
        });
        // Префиксы field (key:/month:/sum:) — несущий контракт: по ним красят ячейки
        // селекторы SupplyScheduleTable.scss и custom.css конфигураций.
        /** Денежная колонка: сумма справа + тултип с позициями периода. */
        const moneyColumn = (
            field: string,
            header: string,
            valueOf: (row: ISupplyScheduleItem) => number | null,
            tooltipContent: (row: ISupplyScheduleItem) => ReactNode | null,
        ): IDataTableColumn<ISupplyScheduleItem> => ({
            field,
            header,
            dataType: ValueType.Double,
            minWidth: 100,
            alignment: 'right',
            accessor: valueOf,
            cellText: (row) => {
                const v = valueOf(row);
                return v != null ? formatPrice(v, 'RUB') : '';
            },
            // в Excel — число, а не текст «100,00 ₽»: по суммам считают и строят сводные
            excelFormat: '#,##0.00',
            render: (row) => {
                const v = valueOf(row);
                const text = v != null ? formatPrice(v, 'RUB') : '';
                const content = tooltipContent(row);
                return content == null ? <span>{text}</span> : sumTooltip(text, content);
            },
        });
        // Год в заголовке месяца — только когда годов несколько: при одном годе
        // «Январь 2026 … Декабрь 2026» дублирует одно и то же 12 раз, а контекст
        // года и так виден в колонке «Итого 2026»
        const multiYear = Object.keys(yearsMonths).length > 1;
        Object.keys(yearsMonths).forEach((year) => {
            const yearMonths = Array.from(yearsMonths[year]);
            yearMonths.forEach((month) => {
                cols.push(
                    moneyColumn(
                        `month:${year}-${month}`,
                        multiYear ? `${capitalize(months.ru[+month - 1])} ${year}` : capitalize(months.ru[+month - 1]),
                        (row) => row.data[year]?.[month]?.sumValue ?? null,
                        (row) => {
                            const positions = row.data[year]?.[month]?.positions;
                            return positions?.length ? positionsTooltip(positions, isAdminView) : null;
                        },
                    ),
                );
            });
            if (yearMonths.length > 1) {
                cols.push(
                    moneyColumn(
                        `sum:${year}`,
                        `Итого ${year}`,
                        (row) =>
                            row.data[year]
                                ? Object.values(row.data[year]).reduce((acc, val) => acc + (val?.sumValue || 0), 0)
                                : null,
                        (row) => {
                            const allPositions = Object.values(row.data[year] ?? {}).flatMap(
                                (monthData) => monthData?.positions ?? [],
                            );
                            return allPositions.length ? (
                                positionsTooltip(allPositions, isAdminView)
                            ) : (
                                <div>Нет позиций для отображения</div>
                            );
                        },
                    ),
                );
            }
        });
        return cols;
    }, [settings, keys, yearsMonths, isAdminView]);

    // Периоды и пустые месяцы живут в меню «Настройки таблицы» (шестерёнка):
    // в тулбаре им не хватало места («Текущий год» обрезался)
    const settingsExtra = (
        <div className="sst-settings">
            <div className="dtb-menu__label">Периоды</div>
            <SegmentedControl
                fullWidth
                size="xs"
                data={YEARS_SEGMENTS}
                value={YEARS_SEGMENTS[yearsShowSetting]?.value}
                onChange={(val) => setYearsShowSetting(+val)}
            />
            <Switch
                label="Пустые месяцы"
                checked={!hideEmptyMonths}
                onCheckedChange={() => setHideEmptyMonths((prev) => !prev)}
                reverse
            />
        </div>
    );

    return (
        <div className="supplyScheduleTable">
            {errorText ? (
                <Hint role="alert" title={`Ошибка загрузки: ${errorText}`} tone="danger" maxWidth="100%" />
            ) : null}
            {settings !== undefined ? (
                items.length > 0 ? (
                    <DataTable<ISupplyScheduleItem>
                        columns={columns}
                        data={items}
                        getRowId={(row) => keys.map((key) => String(row.keyValues[key] ?? '')).join('|')}
                        paging={false}
                        // кэш строк: render ячеек с тултипами позиций дорогой,
                        // без кэша персобирался бы на каждый рендер таблицы
                        cacheRows
                        showFilterRow={false}
                        filterRowToggle
                        showTableSettings
                        tableSettingsExtra={settingsExtra}
                        defaultWrapMode="wrap"
                        // выбор высоты строк переживает
                        // переоткрытие карточки (без ключа он жил до перезагрузки)
                        wrapModeKey={tableKey ? `supply-schedule.${tableKey}` : undefined}
                        exportFileName="График поставки"
                        onCellRendered={markCellValue}
                        data-testid="supplyScheduleTableGrid"
                    />
                ) : (
                    <div className="supplyScheduleTable__empty">
                        <h1>{settings.emptyTextHeader}</h1>
                        <p>{settings.emptyText}</p>
                    </div>
                )
            ) : (
                <div className={'supplyScheduleTable__loading'}>
                    <Preloader position="inline" size="lg" />
                </div>
            )}
        </div>
    );
};

export default SupplyScheduleTable;
